import { z } from 'zod';
// =============================================================================
// Admin: platform pulse
//
// The super-admin dashboard's view of how the platform is being used, read from
// the BigQuery events table: who is active, what they did, and what needs a
// person to look. One response per panel, so a slow or failing panel never
// holds back the rest.
//
// The responses carry DATA, not copy: counters, ids and finite unions. The
// client words them (see `apps/web/admin/src/domains/platform-pulse/`). Names
// stay out too - an `accountId` is resolved to a name by the admin UI, and a
// `userId` is shown as a short id.
//
// Every response carries `generatedAt`: the backend caches each panel for a few
// minutes, so this is when the numbers were read, not when the page was loaded.
// =============================================================================
/**
 * The two windows every panel can show. `today` runs from South African
 * midnight and compares with the same window a week earlier; `7d` is the last
 * seven days against the seven before.
 */
export const PULSE_PERIODS = ['today', '7d'];
export const PulsePeriodSchema = z.enum(PULSE_PERIODS);
/** Every panel takes the same optional filter: one account, or all of them. */
export const PlatformPulseQuerySchema = z.strictObject({
    accountId: z.string().uuid().optional(),
});
// -----------------------------------------------------------------------------
// KPIs
// -----------------------------------------------------------------------------
export const PULSE_KPI_METRICS = [
    'activities',
    'activityValueMinorUnits',
    'activeUsers',
    'activeAccounts',
    'chatReplies',
    'invoices',
    'notifications',
    'notificationsRead',
    'notificationReadRate',
];
export const PulseKpiMetricSchema = z.enum(PULSE_KPI_METRICS);
/**
 * One metric over one period. Either side is null when that window held no
 * events at all (a rate over nothing is null, not zero); a count's null reads
 * as zero. `notificationReadRate` is a 0-1 fraction.
 */
export const PulseKpiSchema = z.object({
    period: PulsePeriodSchema,
    metric: PulseKpiMetricSchema,
    thisPeriod: z.number().nullable(),
    priorPeriod: z.number().nullable(),
});
export const PulseKpisResponseSchema = z.object({
    generatedAt: z.string(),
    rows: z.array(PulseKpiSchema),
});
// -----------------------------------------------------------------------------
// Hourly activity
// -----------------------------------------------------------------------------
/** Active people in one South African clock hour, today and on the same weekday last week. */
export const PulseHourSchema = z.object({
    hour: z.number().int().min(0).max(23),
    today: z.number(),
    lastWeek: z.number(),
});
export const PulseHourlyResponseSchema = z.object({
    generatedAt: z.string(),
    rows: z.array(PulseHourSchema),
});
// -----------------------------------------------------------------------------
// Accounts
// -----------------------------------------------------------------------------
export const PulseAccountSchema = z.object({
    period: PulsePeriodSchema,
    accountId: z.string(),
    users: z.number(),
    activities: z.number(),
    chatReplies: z.number(),
    invoices: z.number(),
    requests: z.number(),
    /** Minutes since a person on the account was last active; null if nobody was. */
    minutesSinceSeen: z.number().nullable(),
});
export const PulseAccountsResponseSchema = z.object({
    generatedAt: z.string(),
    rows: z.array(PulseAccountSchema),
});
// -----------------------------------------------------------------------------
// Capture sources
// -----------------------------------------------------------------------------
export const PulseSourceSchema = z.object({
    period: PulsePeriodSchema,
    /**
     * The activity's `source` value as recorded (`WEB_APP`, `CHAT`...), or null
     * when the event carries none. Not a closed union: an old or new source
     * still shows up, labelled from its raw value.
     */
    source: z.string().nullable(),
    activities: z.number(),
    valueMinorUnits: z.number(),
});
export const PulseSourcesResponseSchema = z.object({
    generatedAt: z.string(),
    rows: z.array(PulseSourceSchema),
});
// -----------------------------------------------------------------------------
// Notifications
// -----------------------------------------------------------------------------
export const PulseNotificationSchema = z.object({
    period: PulsePeriodSchema,
    /** The notification type as recorded (`invoice-overdue`...), or null when absent. */
    type: z.string().nullable(),
    created: z.number(),
    read: z.number(),
    /** Sent on any channel; delivery receipts exist for email alone. */
    sent: z.number(),
    skipped: z.number(),
    failed: z.number(),
});
export const PulseNotificationsResponseSchema = z.object({
    generatedAt: z.string(),
    rows: z.array(PulseNotificationSchema),
});
// -----------------------------------------------------------------------------
// Needs attention
// -----------------------------------------------------------------------------
/** `error`: something broke. `review`: it ran, but its output needs a human to check it. */
export const PULSE_ATTENTION_SEVERITIES = ['error', 'review'];
export const PulseAttentionSeveritySchema = z.enum(PULSE_ATTENTION_SEVERITIES);
export const PULSE_ATTENTION_AREAS = [
    'chat',
    'extraction',
    'email',
    'whatsapp',
    'billing',
    'notification',
    'privacy',
];
export const PulseAttentionAreaSchema = z.enum(PULSE_ATTENTION_AREAS);
/** The event-derived reason a row needs attention. */
export const PULSE_ATTENTION_KINDS = [
    'chat_reply_incomplete',
    'chat_review_faulted',
    'chat_review_failed',
    'chat_tool_input_rejected',
    'extraction_failed',
    'extraction_inconsistent',
    'email_failed',
    'whatsapp_failed',
    'mailbox_subscription_failed',
    'subscription_payment_failed',
    'notification_delivery_failed',
    'privacy_export_failed',
    'privacy_deletion_failed',
];
export const PulseAttentionKindSchema = z.enum(PULSE_ATTENTION_KINDS);
/**
 * One thing to look at. The optional-looking fields are the structured detail
 * the wording needs, each filled only for the kinds that carry it: `failureCode`
 * (incomplete chat reply), `findingCount` (faulted review), `toolName` (rejected
 * tool input), `lineDiscrepancyMinorUnits` (inconsistent extraction), `channel`
 * (failed notification delivery). `conversationId` and `extractionId` are the
 * records the admin pages can open.
 */
export const PulseAttentionSchema = z.object({
    occurredAt: z.string(),
    isToday: z.boolean(),
    kind: PulseAttentionKindSchema,
    severity: PulseAttentionSeveritySchema,
    area: PulseAttentionAreaSchema,
    accountId: z.string().nullable(),
    conversationId: z.string().nullable(),
    extractionId: z.string().nullable(),
    failureCode: z.string().nullable(),
    findingCount: z.number().nullable(),
    toolName: z.string().nullable(),
    lineDiscrepancyMinorUnits: z.number().nullable(),
    channel: z.string().nullable(),
});
export const PulseAttentionResponseSchema = z.object({
    generatedAt: z.string(),
    rows: z.array(PulseAttentionSchema),
});
// -----------------------------------------------------------------------------
// Feed
// -----------------------------------------------------------------------------
export const PULSE_FEED_KINDS = [
    'activity',
    'chat',
    'invoice',
    'banking',
    'classification',
    'supplier',
    'records',
    'notification',
    'access',
];
export const PulseFeedKindSchema = z.enum(PULSE_FEED_KINDS);
/**
 * One run of related work: consecutive events of one kind by one person on one
 * account, with gaps of up to 30 minutes joined. The counters say what happened
 * in it; the client composes the sentence. `userId` is null for system-raised
 * runs (notifications).
 */
export const PulseFeedRunSchema = z.object({
    kind: PulseFeedKindSchema,
    startedAt: z.string(),
    endedAt: z.string(),
    isToday: z.boolean(),
    accountId: z.string().nullable(),
    userId: z.string().nullable(),
    events: z.number(),
    activities: z.number(),
    /** Hours logged by the run's activities that are timed in hours. */
    hours: z.number(),
    activitySources: z.array(z.string()),
    chatReplies: z.number(),
    chatChannels: z.array(z.string()),
    voiceNotes: z.number(),
    invoicesCreated: z.number(),
    invoicesApproved: z.number(),
    invoicesSent: z.number(),
    invoicesPaid: z.number(),
    creditNotes: z.number(),
    payments: z.number(),
    statements: z.number(),
    linesAllocated: z.number(),
    classified: z.number(),
    classifiedAccepted: z.number(),
    supplierInvoices: z.number(),
    extractions: z.number(),
    extractionsFailed: z.number(),
    clients: z.number(),
    matters: z.number(),
    documents: z.number(),
    notifications: z.number(),
    joins: z.number(),
    /** The conversation a chat run belongs to; null for any other kind. */
    conversationId: z.string().nullable(),
});
export const PulseFeedResponseSchema = z.object({
    generatedAt: z.string(),
    rows: z.array(PulseFeedRunSchema),
});
// -----------------------------------------------------------------------------
// People: time in the app, work, page views
//
// One row per person (not per person and account), so someone who works across
// several accounts, or switches into an administered entity, is counted once.
// `accountId` is the account they were busiest on, for context; under the
// account filter every row is about that account alone. Each `prior...` field is
// the same measure over the comparison window, null when the person had none.
// -----------------------------------------------------------------------------
/**
 * Time in the app, ESTIMATED from the request log: requests 30 minutes apart
 * start a new session, and active time sums the gaps of five minutes or less.
 * Background requests the app makes on its own are left out.
 */
export const PulseUserTimeSchema = z.object({
    period: PulsePeriodSchema,
    userId: z.string(),
    /** Null when none of their requests was on an account. */
    accountId: z.string().nullable(),
    accounts: z.number(),
    activeMinutes: z.number(),
    sessions: z.number(),
    /** South African calendar days with a request. */
    daysActive: z.number(),
    priorActiveMinutes: z.number().nullable(),
});
export const PulseUserTimeResponseSchema = z.object({
    generatedAt: z.string(),
    rows: z.array(PulseUserTimeSchema),
});
/** Work a person captured: activities and their value, and invoices created and approved. */
export const PulseUserWorkSchema = z.object({
    period: PulsePeriodSchema,
    userId: z.string(),
    accountId: z.string().nullable(),
    accounts: z.number(),
    activities: z.number(),
    valueMinorUnits: z.number(),
    invoicesCreated: z.number(),
    invoicesApproved: z.number(),
    priorActivities: z.number().nullable(),
    priorValueMinorUnits: z.number().nullable(),
});
export const PulseUserWorkResponseSchema = z.object({
    generatedAt: z.string(),
    rows: z.array(PulseUserWorkSchema),
});
/**
 * Screens opened, each view counted once however often its batch was resent.
 * `route` rows rank screens; `user` rows count each person's views and how many
 * different screens they opened. `route` is a template from the UI usage
 * registry (`/clients/:clientId`), not a URL.
 */
export const PulsePageViewSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('route'),
        period: PulsePeriodSchema,
        route: z.string(),
        views: z.number(),
        users: z.number(),
        priorViews: z.number(),
    }),
    z.object({
        kind: z.literal('user'),
        period: PulsePeriodSchema,
        userId: z.string(),
        accountId: z.string().nullable(),
        views: z.number(),
        routes: z.number(),
        priorViews: z.number(),
    }),
]);
export const PulsePageViewsResponseSchema = z.object({
    generatedAt: z.string(),
    rows: z.array(PulsePageViewSchema),
});
